import { useCallback, useEffect, useRef } from "react";

/**
 * Глобальный перехват скана БЕЗ видимого поля ввода (экономим высоту на ТСД).
 *
 * Как устроено: невидимый sticky-focus input держит фокус, пока `enabled`.
 * Конец скана ловится тремя путями (как на любых ТСД):
 *  1) keydown Enter/Tab — keyboard-wedge с суффиксом;
 *  2) символ-терминатор \r\n\t в значении — IME/commitText-сканеры;
 *  3) пауза-debounce 250 мс — если терминатора нет вовсе.
 * Плюс document-level keydown-буфер как страховка для wedge при потере фокуса
 * (срабатывает только если фокус НЕ на нашем input и НЕ в реальном текстовом поле;
 * буфер считается сканом только при ≥4 символах). ⚠️ На iPhone это часто
 * ОСНОВНОЙ путь: Safari не выполняет `focus()` без жеста пользователя, и
 * скрытый input остаётся без фокуса, пока по экрану не тапнули.
 *
 * Перехват сам не крадёт фокус у настоящих текстовых полей (партия/кол-во/поиск).
 * Пока идёт запрос в 1С / открыта шторка с вводом — передавайте `enabled=false`.
 *
 * ⚠️ `enabled=false` глушит ВСЕ пути, включая скрытый input: он остаётся
 * сфокусированным (его же держал рефокус), и без проверки в момент отправки
 * код со сканера доезжал до `onScan` как ни в чём не бывало — то есть
 * «перехват молчит, пока летит скан / открыта камера» не выполнялось никогда.
 * Но и молча ронять код нельзя: пойманный при выключенном перехвате код
 * уходит в `onBlocked` — экран решает, что сказать человеку. Поэтому
 * `onBlocked` ОБЯЗАТЕЛЕН (тип): экран без него терял скан молча — так «кольцо
 * ничего не сделало» на приёмке возвратов (28.10.2026).
 *
 * Символ берётся из ФИЗИЧЕСКОЙ клавиши (`e.code` + Shift/CapsLock, раскладка
 * US — `charFromCode`), а не из `e.key`: Bluetooth-сканер (кольцо Urovo SR5600
 * на телефоне, HID) шлёт нажатия клавиш, и при русской раскладке телефона
 * `e.key` даёт кириллицу (`215wOktT` → `215цЩлеЕ`). Опознанную клавишу
 * скрытый input дописывает сам (`preventDefault`) — iOS не успевает заменить
 * кавычки на «умные». Клавиша без `code` (IME-сканеры ТСД: `Unidentified`/229)
 * идёт прежним путём — через значение поля / `e.key`.
 */
const TERMINATOR = /[\r\n\t]/;
const DEBOUNCE_MS = 250;
const DOC_MIN_LEN = 4;
const DOC_RESET_MS = 400; // межсимвольная пауза → это новый ввод, буфер сбрасываем

/** Символы клавиш препинания US-раскладки: [без Shift, с Shift]. */
const PUNCT: Record<string, readonly [string, string]> = {
  Minus: ["-", "_"],
  Equal: ["=", "+"],
  BracketLeft: ["[", "{"],
  BracketRight: ["]", "}"],
  Backslash: ["\\", "|"],
  Semicolon: [";", ":"],
  Quote: ["'", '"'],
  Comma: [",", "<"],
  Period: [".", ">"],
  Slash: ["/", "?"],
  Backquote: ["`", "~"],
  Space: [" ", " "],
};
/** Цифровой ряд с Shift (US): `Digit0` → `)`, `Digit1` → `!`, … */
const DIGIT_SHIFTED = ")!@#$%^&*(";
const NUMPAD: Record<string, string> = {
  NumpadDecimal: ".",
  NumpadAdd: "+",
  NumpadSubtract: "-",
  NumpadMultiply: "*",
  NumpadDivide: "/",
  NumpadEqual: "=",
  NumpadComma: ",",
};

/** Нужное `charFromCode` из события клавиатуры (и React, и нативного). */
export interface KeyLike {
  key: string;
  code: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  /** В happy-dom её нет; в браузере и у React-события есть всегда. */
  getModifierState?: (key: "CapsLock") => boolean;
}

/**
 * Символ по ФИЗИЧЕСКОЙ клавише в раскладке US — или `null`, если клавиша не
 * символьная, не опознана (`code` пустой у IME-сканеров ТСД) или нажата с
 * Ctrl/Meta/Alt (это команда, а не ввод).
 *
 * Буквы: регистр = Shift XOR CapsLock. Цифры и препинание CapsLock не трогает.
 */
export function charFromCode(e: KeyLike): string | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  const code = e.code;
  if (!code) return null;
  const shift = e.shiftKey;
  if (/^Key[A-Z]$/.test(code)) {
    const caps = e.getModifierState?.("CapsLock") === true;
    const letter = code.slice(3);
    return shift !== caps ? letter : letter.toLowerCase();
  }
  if (/^Digit[0-9]$/.test(code)) {
    const d = code.slice(5);
    return shift ? DIGIT_SHIFTED[Number(d)]! : d;
  }
  if (/^Numpad[0-9]$/.test(code)) return code.slice(6);
  const numpad = NUMPAD[code];
  if (numpad) return numpad;
  const punct = PUNCT[code];
  if (punct) return shift ? punct[1] : punct[0];
  return null;
}

function isTextEntry(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const t = (el as HTMLInputElement).type;
    return !["button", "submit", "checkbox", "radio", "range", "file", "image", "reset"].includes(t);
  }
  return (el as HTMLElement).isContentEditable === true;
}

export interface ScanCapture {
  /** ref на невидимый input (положить на `<input>`). */
  scanInputRef: React.RefObject<HTMLInputElement>;
  /** пропсы для невидимого input. */
  scanInputProps: {
    inputMode: "none";
    tabIndex: -1;
    "aria-hidden": true;
    autoComplete: "off";
    autoCorrect: "off";
    autoCapitalize: "off";
    spellCheck: false;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onInput: (e: React.FormEvent<HTMLInputElement>) => void;
  };
}

export function useScanCapture({
  enabled,
  onScan,
  onBlocked,
}: {
  enabled: boolean;
  onScan: (raw: string) => void;
  /** Код пришёл при выключенном перехвате: `onScan` не зовётся, но код не
   *  пропал — экран обязан СКАЗАТЬ об этом («Скан не принят» и причина). */
  onBlocked: (raw: string) => void;
}): ScanCapture {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const onBlockedRef = useRef(onBlocked);
  onBlockedRef.current = onBlocked;
  // Решение принимается в МОМЕНТ отправки, а не при подписке: скан кончается
  // на сотни миллисекунд позже, чем начался, и `enabled` за это время меняется.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const emit = useCallback((raw: string) => {
    const v = raw.split(TERMINATOR)[0]!.trim();
    if (!v) return;
    if (!enabledRef.current) {
      onBlockedRef.current(v);
      return;
    }
    onScanRef.current(v);
  }, []);

  const submitFromInput = useCallback(
    (el: HTMLInputElement) => {
      if (flushTimer.current) {
        clearTimeout(flushTimer.current);
        flushTimer.current = null;
      }
      const v = el.value;
      // Чистим ВСЕГДА, в том числе у заблокированного кода: иначе его хвост
      // приклеится к следующему скану.
      el.value = "";
      emit(v);
    },
    [emit],
  );

  const scheduleFlush = useCallback(
    (el: HTMLInputElement) => {
      if (flushTimer.current) clearTimeout(flushTimer.current);
      flushTimer.current = setTimeout(() => submitFromInput(el), DEBOUNCE_MS);
    },
    [submitFromInput],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        submitFromInput(e.currentTarget);
        return;
      }
      if (e.nativeEvent.isComposing) return; // идёт IME-набор — не наше
      const ch = charFromCode(e);
      if (ch === null) return; // IME/ТСД: символ приедет значением поля (onInput)
      // Опознанную клавишу дописываем сами: `input` после `preventDefault`
      // не придёт, поэтому и пауза-debounce перезапускается здесь.
      e.preventDefault();
      const el = e.currentTarget;
      el.value += ch;
      scheduleFlush(el);
    },
    [submitFromInput, scheduleFlush],
  );

  const onInput = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const el = e.currentTarget;
      if (TERMINATOR.test(el.value)) {
        submitFromInput(el);
        return;
      }
      scheduleFlush(el);
    },
    [submitFromInput, scheduleFlush],
  );

  useEffect(
    () => () => {
      if (flushTimer.current) clearTimeout(flushTimer.current);
    },
    [],
  );

  // Фокус держим, только пока перехват включён.
  useEffect(() => {
    if (!enabled) return;
    const node = inputRef.current;

    const refocus = () => {
      const n = inputRef.current;
      if (n && document.activeElement !== n && !isTextEntry(document.activeElement)) {
        n.focus({ preventScroll: true });
      }
    };
    refocus();
    const onBlur = () => requestAnimationFrame(refocus);
    const onVis = () => {
      if (document.visibilityState === "visible") refocus();
    };
    node?.addEventListener("blur", onBlur);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      node?.removeEventListener("blur", onBlur);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [enabled]);

  // Страховочный document-буфер (wedge при потере фокуса). Слушаем ВСЕГДА:
  // отключённый слушатель ронял код молча, а решает про `enabled` — `emit`.
  useEffect(() => {
    let buf = "";
    let last = 0;
    let docTimer: ReturnType<typeof setTimeout> | null = null;
    const flushDoc = () => {
      const v = buf;
      buf = "";
      if (v.length >= DOC_MIN_LEN) emit(v);
    };
    const onDocKey = (e: KeyboardEvent) => {
      const ae = document.activeElement;
      // Основной путь — через input: нажатие, адресованное ему, он разбирает сам
      // (иначе код ушёл бы дважды — буфером и полем).
      if (ae === inputRef.current || e.target === inputRef.current) return;
      if (isTextEntry(ae)) return; // настоящее поле — не перехватываем
      if (e.key === "Enter" || e.key === "Tab") {
        if (docTimer) clearTimeout(docTimer);
        flushDoc();
        return;
      }
      // Физическая клавиша — первой (раскладка не влияет), иначе прежний `e.key`.
      const ch = charFromCode(e) ?? (e.key.length === 1 ? e.key : null);
      if (ch !== null) {
        if (e.timeStamp - last > DOC_RESET_MS) buf = "";
        last = e.timeStamp;
        buf += ch;
        if (docTimer) clearTimeout(docTimer);
        docTimer = setTimeout(flushDoc, DEBOUNCE_MS);
      }
    };
    document.addEventListener("keydown", onDocKey, true);

    return () => {
      document.removeEventListener("keydown", onDocKey, true);
      if (docTimer) clearTimeout(docTimer);
    };
  }, [emit]);

  return {
    scanInputRef: inputRef,
    scanInputProps: {
      inputMode: "none",
      tabIndex: -1,
      "aria-hidden": true,
      autoComplete: "off",
      autoCorrect: "off",
      autoCapitalize: "off",
      spellCheck: false,
      onKeyDown,
      onInput,
    },
  };
}
